import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaCalendarAlt, FaEye, FaPlus } from "react-icons/fa";
import styles from "./AdmissionSchedule.module.css";
import { CustomButton } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { useSelector } from "react-redux";

function AdmissionScheduleList() {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels;

  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_year_id = clientData?.academic_year_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Fetch list on mount and when dependencies change
  useEffect(() => {
    if (school_id) {
      fetchInterviewScheduleList();
    }
  }, [school_id, pagination.pageIndex, pagination.pageSize, apiSearch]);

  // API call to fetch interview schedule list
  const fetchInterviewScheduleList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(
        `/admission/interviewSchedule/list`,
        {
          school_id: school_id,
          search: apiSearch,
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          academic_year_id: Number(academic_year_id),
        },
      );

      if (response?.data?.status) {
        const list = response.data.data || [];
        const formatted = list.map((item, index) => ({
          sno: pagination.pageIndex * pagination.pageSize + index + 1,
          id: item.id,
          academic_year: item.academic_year || "",
          name: item.name || "",
          admission_type: item.admission_type || "",
          start_time: item.start_time || "",
          end_time: item.end_time || "",
          interview_venue: item.interview_venue || "",
          invite_status: item.invite_status || "",
        }));

        setData(formatted);
        setRowCount(response.data.pagination?.total || formatted.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching interview schedule list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate("/user/AdmissionScheduleCreate");
  };

  const handleView = (row) => {
    navigate("/user/view-admission-schedule", {
      state: {
        ID: row.id,
        data: row,
      },
    });
  };

  // Columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "sno",
        header: t?.serialNo || "S.No.",
        size: 70,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "academic_year",
        header: t?.academicYear || "Academic Year",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "name",
        header: t?.name || "Name",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "admission_type",
        header: t?.type || "Type",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "start_time",
        header: t?.startTime || "Start Time",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "end_time",
        header: t?.endTime || "End Time",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "interview_venue",
        header: t?.venue || "Venue",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "invite_status",
        header: t?.inviteStatus || "Invite Status",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: "actions",
        header: t?.actions || "Actions",
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={t?.view || "View"}
          >
            <FaEye />
          </button>
        ),
      },
    ],
    [t],
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaCalendarAlt />
        </div>
        <h1 className={styles.headerTitle}>
          {t?.admissionInterviewSchedule || "Admission Interview Schedule"}
        </h1>
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <CustomButton
          type="button"
          variant="contained"
          className={styles.addButton}
          fullWidth={false}
          onClick={handleAdd}
        >
          <FaPlus style={{ marginRight: "8px" }} />
          {t?.add || "ADD"}
        </CustomButton>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        placeholder={t?.searchName || "Search Name"}
        setSearchInput={setSearchInput}
        onSearchEnter={(value) => {
          setApiSearch(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
        enableColumnFilters={false}
      />
    </div>
  );
}

export default AdmissionScheduleList;
